import { useEffect, useMemo } from "react"

import { Col, Flex, Row, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import TableComponent from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { CHANNEL_SUMMARY_CELLS, channelBreakdownColumns } from "../components/channelColumns"
import ChannelTrendsChart from "../components/ChannelTrendsChart"
import DeflectFilters from "../components/DeflectFilters"
import { withValueSkeleton } from "../components/Skeleton/cardSkeleton"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import { useDeflectScope } from "../hooks/useDeflectScope"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { CHANNEL_COUNT } from "../utils/vocabulary"

import "../../DashboardPage/styles.less"
import tables from "../tables.module.less"

const { Title, Text } = Typography

// The breakdown behind the "Channel trends over time" card: the same chart, with the channel
// figures underneath out of by-channel/ (the donut's answer).
const ChannelsFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getBase, getCompare } = useDeflectScope()
  const byChannel = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_CHANNEL))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_CHANNEL))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_CHANNEL))
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)

  useEffect(() => {
    getCompare(DEFLECT_SECTION.BY_CHANNEL)
  }, [getCompare])

  const summary = byChannel?.summary
  const tableRows = byChannel?.result ?? []
  const columns = useMemo(() => channelBreakdownColumns(t), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])

  const cards = useMemo(
    () => [
      { title: "Lookups", value: summary?.lookups_count ?? 0, icon: "receipt_check" },
      { title: "Lookup volume", value: summary?.fees_amount ?? 0, prefix: "$", icon: "money" },
      { title: "Average volume", value: summary?.avg_fee ?? 0, prefix: "$", icon: "card" },
      {
        title: "Match rate",
        value: summary?.match_rate == null ? null : Math.round(summary.match_rate),
        suffix: "%",
        icon: "pulse"
      }
    ],
    [summary]
  )

  useEffect(() => {
    document.title = "Channel breakdown - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.deflect.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Channel breakdown")}
            </Title>
            <Text type="secondary">
              {t(
                "How your bank inquiries move over time by channel, and the full breakdown underneath. The channel sets the volume charged: $0.50 banking app, $5.00 call center, $0.50 Mastercom."
              )}
            </Text>
          </div>

          <DeflectFilters />
        </Flex>
      </Col>

      <Col span={24}>
        <Row gutter={[24, 24]}>
          {(firstLoad ? withValueSkeleton(cards) : cards).map((card) => (
            <Col key={card.title} xs={24} sm={12} lg={6}>
              <DashboardCard data={card} theme={theme} />
            </Col>
          ))}
        </Row>
      </Col>

      <Col span={24}>
        <ChannelTrendsChart getData={getBase} withViewAll={false} />
      </Col>

      <Col span={24}>
        <TableComponent
          className={tables.olympics}
          dataSource={firstLoad ? skeletonRows(CHANNEL_COUNT) : tableRows}
          columns={firstLoad ? loadingColumns : columns}
          loading={fetching && !firstLoad}
          withOutPagination
          scroll={{}}
          rowKey={firstLoad ? "key" : "channel"}
          tableLayout="fixed"
          showHeader={firstLoad || !!tableRows.length}
          summary={() => (
            <SummaryRows
              summary={tableRows.length && !firstLoad ? summary : null}
              comparisonSummary={byChannel?.comparison_summary}
              comparisonLabel={byChannel?.comparison_period?.label}
              cells={CHANNEL_SUMMARY_CELLS}
            />
          )}
          locale={{
            emptyText: <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
          }}
        />
      </Col>
    </Row>
  )
}

export default ChannelsFullPage
